// @ts-nocheck
import React from 'react';
import { Card as AntCard } from 'antd';
import type { CardProps as AntCardProps } from 'antd';
import { colors, spacing, borderRadius, shadows } from './tokens';
import { getTypographyStyles } from './typography';

export interface CardProps extends Omit<AntCardProps, 'title'> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  variant?: 'default' | 'compact';
  noPadding?: boolean;
  children: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  actions,
  variant = 'default',
  noPadding = false,
  children,
  className = '',
  style,
  ...restProps
}) => {
  const cardStyle: React.CSSProperties = {
    borderRadius: borderRadius.lg,
    border: `1px solid ${colors.border.default}`,
    boxShadow: shadows.sm,
    marginBottom: spacing.lg,
    ...style,
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: colors.background.secondary,
    borderBottom: `1px solid ${colors.border.default}`,
    padding: variant === 'compact' ? `${spacing.sm} ${spacing.lg}` : `${spacing.md} ${spacing.lg}`,
  };

  const bodyStyle: React.CSSProperties = {
    padding: noPadding ? 0 : variant === 'compact' ? spacing.md : spacing.lg,
  };

  const renderTitle = () => {
    if (!title && !subtitle && !actions) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          {title && (
            <div style={{
              ...getTypographyStyles(variant === 'compact' ? 'cardSubtitle' : 'cardTitle'),
              color: colors.text.primary,
            }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{
              ...getTypographyStyles('cardCaption'),
              color: colors.text.secondary,
              marginTop: spacing.xs,
            }}>
              {subtitle}
            </div>
          )}
        </div>
        {actions && <div>{actions}</div>}
      </div>
    );
  };

  return (
    <AntCard
      className={`atlas-card atlas-card-${variant} ${className}`}
      style={cardStyle}
      title={renderTitle()}
      styles={{
        header: title || subtitle || actions ? headerStyle : undefined,
        body: bodyStyle,
      }}
      {...restProps}
    >
      {children}
    </AntCard>
  );
};

// Section component for organizing content within cards
export interface SectionProps {
  title?: string;
  children: React.ReactNode;
  variant?: 'default' | 'bordered';
  className?: string;
  style?: React.CSSProperties;
}

export const Section: React.FC<SectionProps> = ({
  title,
  children,
  variant = 'default',
  className = '',
  style,
}) => {
  const sectionStyle: React.CSSProperties = {
    marginBottom: spacing.lg,
    ...style,
  };

  const titleStyle: React.CSSProperties = {
    ...getTypographyStyles('cardSubtitle'),
    color: colors.text.primary,
    marginBottom: spacing.sm,
  };

  const contentStyle: React.CSSProperties = variant === 'bordered' ? {
    border: `1px solid ${colors.border.default}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    backgroundColor: colors.background.secondary,
  } : {};

  return (
    <div className={`atlas-section atlas-section-${variant} ${className}`} style={sectionStyle}>
      {title && <div style={titleStyle}>{title}</div>}
      <div style={contentStyle}>{children}</div>
    </div>
  );
};

// Info row component for displaying label-value pairs
export interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  emphasis?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  emphasis = false,
  className = '',
  style,
}) => {
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    marginBottom: spacing.sm,
    ...style,
  };

  const labelStyle: React.CSSProperties = {
    ...getTypographyStyles('valueAddress'),
    color: colors.text.secondary,
    minWidth: '120px',
    flexShrink: 0,
  };

  const valueStyle: React.CSSProperties = {
    ...getTypographyStyles(emphasis ? 'cardSubtitle' : 'cardBody'),
    color: colors.text.primary,
  };

  return (
    <div className={`atlas-info-row ${className}`} style={rowStyle}>
      <div style={labelStyle}>{label}:</div>
      <div style={valueStyle}>{value}</div>
    </div>
  );
};

// Table section component for container/goods lists
export interface TableSectionProps {
  title?: string;
  headers: string[];
  rows: React.ReactNode[][];
  actions?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const TableSection: React.FC<TableSectionProps> = ({
  title,
  headers,
  rows,
  actions,
  className = '',
  style,
}) => {
  const sectionStyle: React.CSSProperties = {
    marginBottom: spacing.lg,
    ...style,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  };

  const titleStyle: React.CSSProperties = {
    ...getTypographyStyles('cardSubtitle'),
    color: colors.text.primary,
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyle: React.CSSProperties = {
    ...getTypographyStyles('label'),
    textAlign: 'left',
    padding: spacing.sm,
    borderBottom: `2px solid ${colors.border.default}`,
    color: colors.text.secondary,
  };

  const tdStyle: React.CSSProperties = {
    ...getTypographyStyles('cardBody'),
    padding: spacing.sm,
    borderBottom: `1px solid ${colors.border.default}`,
    color: colors.text.primary,
  };

  return (
    <div className={`atlas-table-section ${className}`} style={sectionStyle}>
      {(title || actions) && (
        <div style={headerStyle}>
          {title && <div style={titleStyle}>{title}</div>}
          {actions && <div>{actions}</div>}
        </div>
      )}
      <table style={tableStyle}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index} style={thStyle}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} style={tdStyle}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};